import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import { SHOPPING_CART_MODE } from "../constants";
import { mappingBookingState2RequestDTO } from "../mapping/mappingBookingState2RequestDTO";
import axiosInstance from "../services/axiosInstance";
import { mappingResponseDTO2BookingState } from "./../mapping/mappingResponseDTO2BookingState";
import {
  useBookingSessionStorage,
  useCartLocalStorage,
} from "./useCartLocalStorage";

const shoudSyncWithDB = false; // TODO: check when cart is sync with db
export const useAddToCart = (props: {
  editBooking?: boolean;
  onSuccess?: () => void;
}) => {
  const cartLocalStorage = useCartLocalStorage();
  const bookingSessionStorage = useBookingSessionStorage();

  return useMutation(
    async ({
      bookGroupNo,
      booking,
      overwrite,
      appendOnly,
    }: {
      bookGroupNo: string;
      booking: BookingState;
      overwrite?: boolean;
      appendOnly?: boolean;
    }) => {
      if (props.editBooking) {
        bookingSessionStorage.add(bookGroupNo, booking, overwrite, appendOnly);
        return;
      }
      if (!shoudSyncWithDB) {
        cartLocalStorage.add(bookGroupNo, booking, overwrite, appendOnly);
        return;
      }

      addToCart([mappingBookingState2RequestDTO(booking)]);
    },
    {
      onSuccess: () => {
        console.log("=== success add to cart ===");
        props.onSuccess?.();
      },
    },
  );
};

export const useRemoveFromCart = (props?: { onSettled?: () => void }) => {
  const cartLocalStorage = useCartLocalStorage();
  const queryClient = useQueryClient();
  return useMutation(
    async (ls: BookingState[]) => {
      for (const d of ls) {
        const id = d.id;
        if (!id) {
          continue;
        }
        if (!shoudSyncWithDB) {
          cartLocalStorage.remove(id);
          continue;
        }
        await removeFromCart(id);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["booking"]);
      },
      onSettled: props?.onSettled,
    },
  );
};

export const useCartBasicInfo = () => {
  const cartLocalStorage = useCartLocalStorage();

  return useQuery<{
    total: number;
  }>(["booking", "basic"], async () => {
    if (!shoudSyncWithDB) {
      return cartLocalStorage.getBasicInfo();
    }
    return fetchCartBasicInfo();
  });
};

export const useCartQuery = () => {
  const cartLocalStorage = useCartLocalStorage();
  const bookingSessionStorage = useBookingSessionStorage();
  const [searchParams] = useSearchParams();
  const bookingIds =
    searchParams
      .get("bookIds")
      ?.split(",")
      .filter((x) => x) ?? [];
  const mode = (searchParams.get("mode") ?? "").toString();
  return useQuery<BookingState[]>(
    ["booking", "total", bookingIds.join(","), mode],
    async () => {
      if (mode === SHOPPING_CART_MODE.UPDATE_BOOKING) {
        return bookingSessionStorage.getAll({ bookingIds });
      }
      if (!shoudSyncWithDB) {
        return cartLocalStorage.getAll({ bookingIds });
      }
      return fetchCart({ bookingIds });
    },
  );
};

export const useBookingStateQuery = ({
  bookingId,
  shouldFetch,
}: {
  bookingId?: string;
  shouldFetch?: boolean;
}) => {
  const cartLocalStorage = useCartLocalStorage();
  return useQuery<BookingState | undefined>({
    queryKey: ["booking", bookingId ?? ""],
    queryFn: async () => {
      if (shoudSyncWithDB || shouldFetch) {
        const { data } = await axiosInstance.get("/bookings/" + bookingId);
        return mappingResponseDTO2BookingState(data.result);
      }
      if (!shoudSyncWithDB) {
        return cartLocalStorage.getAll({ bookingIds: [bookingId ?? ""] })[0];
      }
    },
    enabled: !!bookingId,
    refetchOnWindowFocus: false,
  });
};

// FETCHER

async function fetchCartBasicInfo() {
  // TODO: [CART] api
  if (!shoudSyncWithDB) {
    return {
      total: 0,
    };
  }
  const { data } = await axiosInstance.get("/cart/basic-info");
  return data;
}
async function fetchCart({ bookingIds }: { bookingIds?: string[] }) {
  const { data } = await axiosInstance.get("/cart");
  return data;
}
async function addToCart(pl: BookingRequestDTO[]) {
  console.log(pl);
  // TODO: [CART] api
  alert("ADD TO CART");
  return;

  const { data } = await axiosInstance.post("/cart");
  return data;
}
async function removeFromCart(id: string) {
  // TODO: [CART] api
  const { data } = await axiosInstance.delete(`/cart/${id}`);
  return data;
}
